import type { APIError } from '@vizit/api';
import { BulkImportDialog } from '@vizit/organisms';
import { useRetailerRegion } from '../../hooks/use-retailer-region';
import { useProductBulkImportDialog } from './product-bulk-import-dialog.hooks';

interface ProductBulkImportDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen?: boolean;
  /**
   * Callback when dialog open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally
   */
  hideTrigger?: boolean;
  /**
   * The ID of the portfolio to import products to
   */
  portfolioId?: string;
  /**
   * Callback when bulk import is successful
   */
  onSuccessfulImport?: (count: number) => void;
  /**
   * Callback when an error occurs during import
   */
  onImportError?: (error: unknown | APIError) => void;
}

/**
 * Product bulk import dialog component that wraps the BulkImportDialog organism
 * with product-specific business logic.
 *
 * @example
 * <ProductBulkImportDialog
 *   portfolioId="123"
 *   onSuccessfulImport={(count) => console.log('Import successful')}
 *   onImportError={(error) => console.error('Import error', error)}
 * />
 */
function ProductBulkImportDialog(props: ProductBulkImportDialogProps) {
  const { isOpen, onOpenChange, hideTrigger = false, portfolioId, onSuccessfulImport, onImportError } = props;

  const { data: retailerRegionData } = useRetailerRegion();

  const { onSubmit } = useProductBulkImportDialog({
    portfolioId,
    onSuccessfulImport,
    onImportError,
  });

  return (
    <BulkImportDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      triggerButtonContent={hideTrigger ? undefined : 'Bulk import products'}
      hideTrigger={hideTrigger}
      retailer={retailerRegionData?.retailer}
      region={retailerRegionData?.region}
      itemIdLabel={retailerRegionData?.item_id_label ? `${retailerRegionData.item_id_label}s` : undefined}
    />
  );
}

export { ProductBulkImportDialog };
export type { ProductBulkImportDialogProps };
